import React, { useEffect, useRef, useState } from "react";
import { XIcon } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { WebService } from "../../services/WebService";
import { toast } from "react-toastify";
import { HiDotsVertical } from "react-icons/hi";
import Button from "@mui/material/Button";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { useNotifyCount } from "../../store/Notifycount";
import useModalCloseHook from "../../hooks/useModalCloseHook";
import { ADMIN, PROJECT_COORDINATOR } from "../../constants/roles";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

const Notification = ({ setIsNotification }) => {
  const modalRef = useRef(null);
  const actionRef = useRef(null);

  const navigate = useNavigate();
  const user = useSelector((state) => state?.auth?.user);
  const { removeAllNotifyCount, decreaseNotifyCount } = useNotifyCount();

  const [notifications, setNotifications] = useState([]);
  const [totalPages, setTotalpages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [date, setDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [isPerformAction, setIsPerformAction] = useState(null);

  const handleClose = () => setIsNotification(false);
  useModalCloseHook(modalRef, handleClose);

  const getShortName = (msg) => {
    const keyword = "uploaded";
    const index = msg.indexOf(keyword);
    if (index === -1) return "";
    const name = msg.substring(0, index).trim().split(" ");
    return (
      (name[0]?.charAt(0).toUpperCase() || "") +
      (name.length > 1
        ? name[name.length - 1]?.charAt(0).toUpperCase()
        : "")
    );
  };

  const handleNavigate = (project) => {
    if (user.role === ADMIN) {
      navigate(`/view-project/${project.id}`, {
        state: { projectName: project.projectName },
      });
      setIsNotification(false);
    } else if (user.role === PROJECT_COORDINATOR) {
      navigate(`/emp-projects-details/${project.id}`, {
        state: { projectName: project.projectName },
      });
      setIsNotification(false);
    } else {
      navigate(`/client-project-details/${project.id}`, {
        state: { projectName: project.projectName },
      });
      setIsNotification(false);
    }
  };

  const handleNotifyCount = async (id, isRead) => {
    if (isRead) return;
    try {
      await WebService.markNotificationAsRead(id);
      decreaseNotifyCount();
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleRemoveAllNotifyCount = async () => {
    if (!notifications.length) return;
    try {
      await WebService.markAllNotificationAsRead();
      removeAllNotifyCount();
      setNotifications([]);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteNotification = async (id, isRead) => {
    try {
      await WebService.deleteNotification(id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      if (!isRead) decreaseNotifyCount();
    } catch (error) {
      toast.error(error.message);
    }
  };

  useEffect(() => {
    const fetchNotification = async () => {
      try {
        setLoading(true);
        const message = await WebService.getNotification(
          currentPage,
          10,
          date,
          "document"
        );
        if (message) {
          setNotifications(message.data);
          setTotalpages(message.pagination.totalPages);
        }
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchNotification();
  }, [currentPage, date]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (actionRef.current && !actionRef.current.contains(e.target)) {
        setIsPerformAction(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative">
      <AnimatePresence>
        <motion.div
          ref={modalRef}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="
            fixed sm:absolute right-0 sm:right-2 top-14 sm:top-auto
            w-full sm:w-[450px] xl:w-[550px]
            max-h-[90vh]
            bg-white rounded-xl shadow-2xl z-50
          "
        >
{/* Header */}
<div className="relative px-4 py-3 border-b">
  <h3 className="text-lg font-semibold text-gray-800">
    Notifications
  </h3>

  {/* Close Button - FIXED TOP RIGHT */}
  <button
    onClick={handleClose}
    className="absolute top-3 right-3"
  >
    <XIcon className="w-6 h-6 text-gray-500 hover:text-gray-700" />
  </button>

  <div className="mt-3 flex flex-col sm:flex-row gap-2">
    <input
      type="date"
      value={date}
      onChange={(e) => setDate(e.target.value)}
      className="outline-none border border-zinc-400 rounded px-2 text-sm w-full sm:w-auto"
    />

    <Button
      variant="outlined"
      onClick={handleRemoveAllNotifyCount}
      className="!text-sm !capitalize w-full sm:w-auto"
    >
      Mark all as read
    </Button>
  </div>
</div>


          {/* List */}
          <div className="max-h-[65vh] overflow-y-auto divide-y">
            {loading ? (
              <p className="py-10 text-center text-primary">Loading...</p>
            ) : notifications.length === 0 ? (
              <p className="py-10 text-center text-gray-400">
                No notifications available.
              </p>
            ) : (
              notifications.map((note) => (
                <div
                  key={note.id}
                  onClick={() => handleNavigate(note.project)}
                  className={`flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-gray-200 ${
                    !note.isRead ? "bg-gray-100" : ""
                  }`}
                >
                  <div className="h-10 w-10 shrink-0 bg-primary/10 text-primary rounded-full flex items-center justify-center font-bold text-sm">
                    {getShortName(note.message)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-800 break-words line-clamp-2">
                      {note.message}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(note.createdAt).toLocaleString()}
                    </p>
                  </div>

                  <div
                    className="relative shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <HiDotsVertical
                      onClick={() =>
                        setIsPerformAction(
                          isPerformAction === note.id ? null : note.id
                        )
                      }
                      className="w-5 h-5 text-gray-500"
                    />

                    {isPerformAction === note.id && (
                      <div
                        ref={actionRef}
                        className="absolute top-6 right-0 w-[160px] bg-white shadow-lg border rounded-md z-10"
                      >
                        <button
                          disabled={note.isRead}
                          onClick={() =>
                            handleNotifyCount(note.id, note.isRead)
                          }
                          className="w-full px-4 py-2 text-sm hover:bg-gray-100"
                        >
                          Mark as Read
                        </button>
                        <button
                          onClick={() =>
                            handleDeleteNotification(note.id, note.isRead)
                          }
                          className="w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {notifications.length > 0 && (
            <div className="flex justify-between sm:justify-end items-center gap-2 px-4 py-2 border-t">
              <button
                onClick={() => setCurrentPage((p) => p - 1)}
                disabled={currentPage === 1}
                className="h-8 w-8 flex items-center justify-center rounded-full bg-primary/10 disabled:bg-gray-200"
              >
                <MdKeyboardArrowLeft />
              </button>

              <span className="text-sm">
                Page {currentPage} of {totalPages}
              </span>

              <button
                onClick={() => setCurrentPage((p) => p + 1)}
                disabled={currentPage === totalPages}
                className="h-8 w-8 flex items-center justify-center rounded-full bg-primary/10 disabled:bg-gray-200"
              >
                <MdKeyboardArrowRight />
              </button>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default Notification;
